import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CloseButton,
  Group,
  LoadingOverlay,
  Pagination,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { IconFilterOff, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { getOrganizationsPaginated } from "@utils/api-communication/RestApiCli";
import { accountTypeValues } from "@constants/orgConstants";
import { withDemoAndDistributorTypes } from "@constants/accountTypeOptions";
import { checkRole } from "@utils/CommonUtil";
import { Role } from "@model/role";
import OrganizationsTable from "./OrganizationsTable";
import {
  buildOrganizationListQuery,
  DeviceFilter,
  EMPTY_ORGANIZATION_FILTERS,
  hasActiveFilters,
  ORGANIZATION_LIST_PAGE_SIZE,
  ORGANIZATION_SEARCH_MAX_LENGTH,
  OrganizationListFilters,
  OrganizationListRow,
  parseOrganizationListResponse,
  toAccountType,
} from "./organizationListQuery";

export interface DistributorOption {
  value: string;
  label: string;
}

interface PaginatedOrganizationsProps {
  distId?: string | null;
  showDetails: boolean;
  showCountry?: boolean;
  showDistId?: boolean;
  dateLocale?: string;
  onOrganizationSelect?: (organization: OrganizationListRow) => void;
  onDistSelect?: (distId: string) => void;
  distributorOptions?: DistributorOption[];
}

const SEARCH_DEBOUNCE_MS = 300;

const PaginatedOrganizations: React.FC<PaginatedOrganizationsProps> = ({
  distId,
  showDetails,
  showCountry = false,
  showDistId = false,
  dateLocale,
  onOrganizationSelect,
  onDistSelect,
  distributorOptions,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filters, setFilters] = useState<OrganizationListFilters>(EMPTY_ORGANIZATION_FILTERS);
  const [pageState, setPageState] = useState({ page: 1, search: "" });
  const [rows, setRows] = useState<OrganizationListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const page = pageState.search === appliedSearch ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ page: next, search: appliedSearch });

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const scopedFilters = useMemo(
    () => (distId ? { ...filters, distId } : filters),
    [filters, distId],
  );

  const fetchPage = useCallback(() => {
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    getOrganizationsPaginated(
      buildOrganizationListQuery(scopedFilters, appliedSearch, page, ORGANIZATION_LIST_PAGE_SIZE),
      (response) => {
        if (requestId !== requestIdRef.current) return;
        const parsed = parseOrganizationListResponse(response?.data);
        if (!parsed) {
          console.error("[PaginatedOrganizations] Malformed organization list response");
          setError(true);
          setLoading(false);
          return;
        }
        setRows(parsed.data);
        setTotal(parsed.total);
        setLastPage(parsed.lastPage);
        setLoading(false);
      },
      (fetchError) => {
        if (requestId !== requestIdRef.current) return;
        console.error("[PaginatedOrganizations] Failed to load organizations:", fetchError);
        setError(true);
        setLoading(false);
      },
      controller.signal,
    );
    return () => controller.abort();
  }, [scopedFilters, appliedSearch, page]);

  useEffect(() => fetchPage(), [fetchPage]);

  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  const changeSearch = (value: string) => {
    setSearch(value);
    if (value === "") setAppliedSearch("");
  };

  const changeFilter = <K extends keyof OrganizationListFilters>(
    key: K,
    value: OrganizationListFilters[K],
  ) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetAll = () => {
    setPage(1);
    setSearch("");
    setAppliedSearch("");
    setFilters(EMPTY_ORGANIZATION_FILTERS);
  };

  const accountTypeOptions = withDemoAndDistributorTypes(
    accountTypeValues,
    checkRole([Role.Admin]),
  ).map((value) => ({
    value,
    label: value.toUpperCase(),
  }));
  const deviceOptions: { value: DeviceFilter; label: string }[] = [
    { value: "any", label: t("organizationsList.devicesAny") },
    { value: "with", label: t("organizationsList.devicesWith") },
    { value: "without", label: t("organizationsList.devicesWithout") },
  ];
  const filtersActive = hasActiveFilters(filters, search);
  const rowOffset = (page - 1) * ORGANIZATION_LIST_PAGE_SIZE;

  return (
    <Box pos="relative">
      <Group gap="sm" align="flex-end" mb="sm" wrap="wrap">
        <TextInput
          flex={1}
          miw={260}
          placeholder={t("organizationsList.searchPlaceholder")}
          leftSection={<IconSearch size={16} />}
          value={search}
          maxLength={ORGANIZATION_SEARCH_MAX_LENGTH}
          onChange={(event) => changeSearch(event.currentTarget.value)}
          rightSection={
            search ? (
              <CloseButton
                size="sm"
                aria-label={t("organizationsList.clearSearch")}
                onClick={() => changeSearch("")}
              />
            ) : null
          }
          data-testid="organizations-search"
        />
        {distributorOptions && (
          <Select
            w={220}
            placeholder={t("organizationsList.filterDistributor")}
            aria-label={t("organizationsList.filterDistributor")}
            data={distributorOptions}
            value={filters.distId}
            onChange={(value) => changeFilter("distId", value)}
            searchable
            clearable
            data-testid="organizations-filter-distributor"
          />
        )}
        <Select
          w={180}
          placeholder={t("organizationsList.filterAccountType")}
          aria-label={t("organizationsList.filterAccountType")}
          data={accountTypeOptions}
          value={filters.accountType}
          onChange={(value) => changeFilter("accountType", toAccountType(value))}
          searchable
          clearable
          data-testid="organizations-filter-account-type"
        />
        <Select
          w={180}
          aria-label={t("organizationsList.filterDevices")}
          data={deviceOptions}
          value={filters.devices}
          onChange={(value) => changeFilter("devices", (value as DeviceFilter) ?? "any")}
          allowDeselect={false}
          data-testid="organizations-filter-devices"
        />
        <Button
          variant="subtle"
          leftSection={<IconFilterOff size={16} />}
          onClick={resetAll}
          disabled={!filtersActive}
          data-testid="organizations-reset-filters"
        >
          {t("organizationsList.resetFilters")}
        </Button>
      </Group>

      <Box pos="relative" mih={80}>
        <LoadingOverlay visible={loading} zIndex={10} overlayProps={{ radius: "sm", blur: 1 }} />
        {error ? (
          <Alert color="red" variant="light">
            {t("organizationsList.loadError")}
          </Alert>
        ) : (
          <>
            <OrganizationsTable
              organizations={rows}
              showDetails={showDetails}
              onOrganizationSelect={(orgId) => {
                const organization = rows.find((row) => row.orgId === orgId);
                if (organization && onOrganizationSelect) onOrganizationSelect(organization);
              }}
              onDistSelect={onDistSelect}
              showCountry={showCountry}
              showDistId={showDistId}
              dateLocale={dateLocale}
              rowOffset={rowOffset}
            />
            {!loading && rows.length === 0 && (
              <Text ta="center" c="var(--oculera-text-secondary)" py="md">
                {t(filtersActive ? "organizationsList.noResults" : "organizationsList.empty")}
              </Text>
            )}
          </>
        )}
      </Box>

      <Group justify="space-between" mt="sm">
        <Text size="sm" c="var(--oculera-text-secondary)" data-testid="organizations-summary">
          {t("organizationsList.summary", { count: total })}
          {total > 0 && ` · ${t("organizationsList.pageOf", { page, lastPage })}`}
        </Text>
        <Pagination value={page} onChange={setPage} total={lastPage} withEdges />
      </Group>
    </Box>
  );
};

export default PaginatedOrganizations;
